import clarityLogoLight from "@/assets/clarity-logo-light.svg";
import type { ReactNode } from "react";
import { CheckCircle2, Download, MessageCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { CLARITY_DOC_COLORS } from "@shared/clarityDocumentTokens";
import {
  formatInvoiceMoneyDollar,
  formatInvoiceMoneySgd,
  presentInvoiceGstBreakdown,
} from "@shared/invoiceGstPresentation";
import { buildPayNowUenFixedAmountPayload } from "@shared/payNowSgqr";

const DOC = CLARITY_DOC_COLORS;
const UEN = "200208768C";

function formatDate(ts: number | string | Date | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function normalisePhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("65")) return digits;
  if (digits.startsWith("8") || digits.startsWith("9")) return `65${digits}`;
  return digits;
}

export type InvoiceLine = {
  description: string;
  total?: number | null;
  qty?: number | null;
  quantity?: number | null;
  unitAmount?: number | null;
  proposalUrl?: string | null;
  billingScheduleUrl?: string | null;
};

export type InvoiceViewModel = {
  invoiceNumber: string;
  invoiceStatus: string;
  invoiceDate?: string | Date | number | null;
  paidAt?: string | Date | number | null;
  dueDate?: string | null;
  paymentTerms?: string | null;
  proposalRef?: string | null;
  paymentStageLabel?: string | null;
  customerName: string;
  customerContactPerson?: string | null;
  billingAddress?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  picName?: string | null;
  picPhone?: string | null;
  sourceRef?: string | null;
  sourceLine?: string | null;
  sourceLabel?: string | null;
  description?: string | null;
  lineItems?: InvoiceLine[] | null;
  approvedProposalUrl?: string | null;
  approvedProposalFileName?: string | null;
  gstApplied: boolean;
  subtotal: number;
  gstAmount: number;
  total: number;
  payNowReference?: string | null;
  paymentLink?: string | null;
  cardPaymentLink?: string | null;
  cardPaymentUnavailableMessage?: string | null;
  cardSurchargeText?: string | null;
  hasPdf?: boolean;
  onDownloadPdf?: () => void | Promise<void>;
  onPrintPdf?: () => void;
  amountPaidInclGst?: number | null;
  creditAppliedAmount?: number | null;
  paymentOnHold?: boolean;
  amountOutstandingInclGst?: number | null;
};

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-[10px] font-semibold uppercase tracking-[0.08em] mb-1"
      style={{ color: DOC.muted }}
    >
      {children}
    </p>
  );
}

/**
 * Hosted tax invoice — same commercial document layout as the invoice PDF.
 * Paid invoices keep the invoice composition and hide payment options.
 */
export function InvoiceView({ vm }: { vm: InvoiceViewModel }) {
  const isPaid = vm.invoiceStatus === "paid";
  const isPartial = vm.invoiceStatus === "partial";
  const payNowRef = (vm.payNowReference ?? vm.invoiceNumber) || "—";
  const dueForPayNow =
    vm.amountOutstandingInclGst != null && vm.amountOutstandingInclGst >= 0
      ? vm.amountOutstandingInclGst
      : vm.total;
  const payNowQrValue =
    !isPaid && !vm.paymentOnHold && dueForPayNow > 0
      ? buildPayNowUenFixedAmountPayload({ uen: UEN, amountSgd: dueForPayNow, billNumber: payNowRef })
      : null;
  const phone = normalisePhone(vm.picPhone);
  const waLink = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(`Hi ${vm.picName ?? "team"}, I have a question regarding invoice ${vm.invoiceNumber}.`)}`
    : null;

  const gstPresentation = presentInvoiceGstBreakdown({
    amountInclGst: vm.total,
    gstApplied: Boolean(vm.gstApplied),
  });

  const rows =
    vm.lineItems && vm.lineItems.length
      ? vm.lineItems
      : [{ description: vm.description || "Invoice item", total: gstPresentation.lineUnitAmount, qty: 1 }];

  const proposalRef = vm.proposalRef || vm.sourceRef || null;
  const paymentStage = vm.paymentStageLabel?.trim() || null;

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" style={{ backgroundColor: "#EDEBE6" }}>
      <article
        className="mx-auto w-full max-w-[720px] px-8 sm:px-12 py-10 sm:py-14"
        style={{ backgroundColor: DOC.cream, color: DOC.navy }}
      >
        {/* Letterhead: logo + company block (matches invoice PDF) */}
        <header className="flex items-start gap-6 sm:gap-8">
          <img src={clarityLogoLight} alt="Clarity" className="h-7 object-contain shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold tracking-wide">CLARITY AV PTE LTD</p>
            <p className="mt-1 text-[10px] leading-relaxed" style={{ color: DOC.desc }}>
              Showroom: No. 1 Coleman Street, #03-36 The Adelphi, Singapore 179803
              <br />
              Clarity Club: 201 Henderson Road, #04-19 Apex @ Henderson, Singapore 159545
              <br />
              GST Reg No: {UEN}
              <br />
              Tel: +65 6333-0010
            </p>
          </div>
        </header>

        <div className="mt-6 h-[1.5px] w-full" style={{ backgroundColor: DOC.accent }} />

        {/* Title + invoice number */}
        <div className="mt-5 flex items-start justify-between gap-6">
          <div>
            <p className="text-[15px] font-bold tracking-[0.08em]">TAX INVOICE</p>
            {(isPartial || isPaid) && (
              <p
                className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em]"
                style={{ color: isPaid ? "#15803d" : "#B45309" }}
              >
                {isPartial ? "Partial" : "Paid"}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: DOC.muted }}>
              Invoice Number:
            </p>
            <p className="mt-1 text-[12px] font-semibold">{vm.invoiceNumber}</p>
            {isPaid && vm.paidAt ? (
              <p className="mt-1 text-[10px]" style={{ color: DOC.desc }}>
                Paid {formatDate(vm.paidAt)}
              </p>
            ) : null}
          </div>
        </div>

        {isPartial && (
          <p className="mt-5 text-[12px]" style={{ color: "#92400E" }}>
            {Number(vm.creditAppliedAmount ?? 0) > 0 ? "Credit applied — outstanding balance remains." : "Partial payment recorded — outstanding balance remains."}
          </p>
        )}
        {isPaid && (
          <p className="mt-5 text-[12px] flex items-center gap-2" style={{ color: "#15803d" }}>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            {Number(vm.creditAppliedAmount ?? 0) > 0 ? "Invoice settled with customer credit — thank you." : "Payment received — Thank you!"}
          </p>
        )}

        {/* Bill To + metadata (PDF two-column) */}
        <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          <div className="space-y-5">
            <div>
              <FieldLabel>Bill To</FieldLabel>
              <p className="text-[13px] font-semibold">{vm.customerName}</p>
              {vm.customerContactPerson ? (
                <p className="text-[11px] mt-1" style={{ color: DOC.desc }}>
                  {vm.customerContactPerson}
                </p>
              ) : null}
              {vm.billingAddress ? (
                <p className="text-[11px] whitespace-pre-line mt-1 leading-relaxed" style={{ color: DOC.desc }}>
                  {vm.billingAddress}
                </p>
              ) : null}
              {vm.customerEmail ? (
                <p className="text-[11px]" style={{ color: DOC.desc }}>
                  {vm.customerEmail}
                </p>
              ) : null}
              {vm.customerPhone ? (
                <p className="text-[11px]" style={{ color: DOC.desc }}>
                  {vm.customerPhone}
                </p>
              ) : null}
            </div>
            <div>
              <FieldLabel>Consultant</FieldLabel>
              <p className="text-[12px]">{vm.picName || "Clarity Service Team"}</p>
            </div>
          </div>

          <div className="space-y-4">
            {proposalRef ? (
              <div>
                <FieldLabel>Reference</FieldLabel>
                <p className="text-[12px]">{proposalRef}</p>
              </div>
            ) : null}
            <div>
              <FieldLabel>Invoice Date</FieldLabel>
              <p className="text-[12px]">{formatDate(vm.invoiceDate)}</p>
            </div>
            <div>
              <FieldLabel>{paymentStage ? "Payment Stage" : "Due Date"}</FieldLabel>
              <p className="text-[12px]">
                {paymentStage || vm.dueDate || "Payment due immediately"}
              </p>
            </div>
            <div>
              <FieldLabel>Currency</FieldLabel>
              <p className="text-[12px]">SGD</p>
            </div>
          </div>
        </section>

        {/* Line items — DESCRIPTION / QTY / UNIT PRICE / AMOUNT */}
        <section className="mt-10">
          <div
            className="grid gap-2 pb-2 border-b text-[9px] font-semibold uppercase tracking-[0.08em]"
            style={{
              borderColor: DOC.navy,
              color: DOC.muted,
              gridTemplateColumns: "minmax(0,1.6fr) 0.35fr 0.9fr 0.7fr",
            }}
          >
            <span>Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right leading-tight">{gstPresentation.unitPriceHeader}</span>
            <span className="text-right">Amount</span>
          </div>

          {rows.map((row, idx) => {
            const qtyRaw = row.quantity ?? row.qty;
            const qty = qtyRaw != null && Number(qtyRaw) > 0 ? Number(qtyRaw) : 1;
            // Single primary line: prefer PDF GST presentation amounts so hosted matches tax invoice.
            const useGstPresentationLine =
              rows.length === 1 ||
              (rows.length === 2 && idx === 0 && /card payment|card fee|processing fee/i.test(String(rows[1]?.description ?? "")));
            const lineAmount = useGstPresentationLine
              ? gstPresentation.lineUnitAmount
              : row.total != null && Number.isFinite(Number(row.total))
                ? Number(row.total)
                : gstPresentation.lineUnitAmount;
            const unitAmount =
              row.unitAmount != null && Number.isFinite(Number(row.unitAmount)) && !useGstPresentationLine
                ? Number(row.unitAmount)
                : qty > 0
                  ? lineAmount / qty
                  : lineAmount;
            return (
              <div
                key={`${row.description}-${idx}`}
                className="py-4 border-b"
                style={{ borderColor: DOC.rowBorder }}
              >
                <div
                  className="grid gap-2 text-[12px] items-start"
                  style={{ gridTemplateColumns: "minmax(0,1.6fr) 0.35fr 0.9fr 0.7fr" }}
                >
                  <div className="min-w-0">
                    <p className="whitespace-pre-wrap break-words" style={{ color: DOC.navy }}>
                      {row.description}
                    </p>
                    {(row.proposalUrl || row.billingScheduleUrl || (idx === 0 && vm.approvedProposalUrl)) && (
                      <div className="mt-2 space-y-1 text-[11px]">
                        {(row.proposalUrl || (idx === 0 ? vm.approvedProposalUrl : null)) ? (
                          <a
                            href={(row.proposalUrl || vm.approvedProposalUrl)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                            style={{ color: DOC.navy }}
                          >
                            Click here to see approved proposal.
                          </a>
                        ) : null}
                        {row.billingScheduleUrl ? (
                          <a
                            href={row.billingScheduleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline block"
                            style={{ color: DOC.navy }}
                          >
                            View Original Billing Schedule
                          </a>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <span className="text-right tabular-nums">{qty}</span>
                  <span className="text-right tabular-nums">{formatInvoiceMoneyDollar(unitAmount)}</span>
                  <span className="text-right font-semibold tabular-nums">
                    {formatInvoiceMoneyDollar(lineAmount)}
                  </span>
                </div>
              </div>
            );
          })}

          <div className="mt-6 ml-auto w-full max-w-[280px] space-y-2.5 text-[12px]">
            <div className="flex justify-between gap-6" style={{ color: DOC.muted }}>
              <span>Subtotal (excl. GST)</span>
              <span className="tabular-nums" style={{ color: DOC.navy }}>
                {formatInvoiceMoneyDollar(gstPresentation.subtotalExclGst)}
              </span>
            </div>
            <div className="flex justify-between gap-6" style={{ color: DOC.muted }}>
              <span>GST (9%)</span>
              <span className="tabular-nums" style={{ color: DOC.navy }}>
                {formatInvoiceMoneyDollar(gstPresentation.gstAmount)}
              </span>
            </div>
            <div className="h-[1.5px] w-full" style={{ backgroundColor: DOC.accent }} />
            <div className="flex justify-between gap-6 items-baseline pt-0.5">
              <span className="text-[11px] font-semibold" style={{ color: DOC.muted }}>
                TOTAL (incl. GST)
              </span>
              <span className="text-[15px] font-bold tabular-nums" style={{ color: DOC.navy }}>
                {formatInvoiceMoneySgd(gstPresentation.totalInclGst)}
              </span>
            </div>
            {(isPartial || Number(vm.creditAppliedAmount ?? 0) > 0 || (vm.amountPaidInclGst != null && vm.amountPaidInclGst > 0.004)) && (
              <>
                <div className="flex justify-between gap-6 pt-2" style={{ color: DOC.desc }}>
                  <span>Cash received</span>
                  <span className="tabular-nums font-medium" style={{ color: DOC.navy }}>
                    {formatInvoiceMoneySgd(vm.amountPaidInclGst ?? 0)}
                  </span>
                </div>
                {Number(vm.creditAppliedAmount ?? 0) > 0 && <div className="flex justify-between gap-6"><span>Credit applied</span><span className="tabular-nums">{formatInvoiceMoneySgd(vm.creditAppliedAmount ?? 0)}</span></div>}
                <div className="flex justify-between gap-6 font-semibold">
                  <span>Outstanding</span>
                  <span className="tabular-nums text-[14px]">
                    {formatInvoiceMoneySgd(vm.amountOutstandingInclGst ?? dueForPayNow)}
                  </span>
                </div>
              </>
            )}
          </div>
        </section>

        {vm.sourceLabel ? (
          <p className="mt-6 text-[11px]" style={{ color: DOC.muted }}>
            {vm.sourceLabel}
          </p>
        ) : null}
        {vm.sourceLine ? (
          <p className="text-[11px]" style={{ color: DOC.muted }}>
            {vm.sourceLine}
          </p>
        ) : null}

        {/* Payment instructions — unpaid / partial only */}
        {vm.paymentOnHold && <p className="mt-6 text-sm">Payments are temporarily paused while Finance reconciles a credit application.</p>}
        {!isPaid && !vm.paymentOnHold && (
          <section className="mt-12">
            <div className="h-[1.5px] w-full" style={{ backgroundColor: DOC.accent }} />
            <p
              className="mt-5 text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: DOC.accent }}
            >
              Payment Options
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-10 items-start">
              <div className="space-y-8 min-w-0">
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <p className="text-[12px] font-semibold">1) PAYNOW</p>
                    <span className="text-[10px] font-medium tracking-wide" style={{ color: DOC.accent }}>
                      Preferred
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed mb-3" style={{ color: DOC.desc }}>
                    Scan with your banking app. Amount and reference are pre-filled.
                  </p>
                  <div className="space-y-1.5 text-[11px]" style={{ color: DOC.desc }}>
                    <p>
                      UEN: <span style={{ color: DOC.navy }}>{UEN}</span>
                    </p>
                    <p>
                      Ref: <span style={{ color: DOC.navy }}>{payNowRef}</span>
                    </p>
                    <p>
                      Amount:{" "}
                      <span style={{ color: DOC.navy }}>{formatInvoiceMoneySgd(dueForPayNow)}</span>
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-[12px] font-semibold mb-3">2) CREDIT CARD</p>
                  {vm.cardPaymentLink ? (
                    <a
                      href={vm.cardPaymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-semibold underline"
                      style={{ color: DOC.navy }}
                    >
                      CLICK HERE TO PAY VIA CREDIT CARD
                    </a>
                  ) : vm.cardPaymentUnavailableMessage ? (
                    <p
                      className="text-[11px] leading-relaxed uppercase tracking-wide"
                      style={{ color: DOC.desc }}
                    >
                      {vm.cardPaymentUnavailableMessage}
                    </p>
                  ) : (
                    <p className="text-[11px] italic" style={{ color: DOC.muted }}>
                      Credit card link unavailable.
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-[12px] font-semibold mb-3">3) BANK TRANSFER</p>
                  <dl className="space-y-2 text-[11px]" style={{ color: DOC.desc }}>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Bank</dt>
                      <dd className="text-right">United Overseas Bank Limited</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Account Name</dt>
                      <dd className="text-right font-medium" style={{ color: DOC.navy }}>
                        Clarity AV Pte Ltd
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Account No. (SGD)</dt>
                      <dd className="text-right font-medium" style={{ color: DOC.navy }}>
                        372-302-363-5
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Account No. (USD)</dt>
                      <dd className="text-right font-medium" style={{ color: DOC.navy }}>
                        372-903-911-8
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Bank / Branch</dt>
                      <dd className="text-right">7375 / 046</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Swift</dt>
                      <dd className="text-right font-medium" style={{ color: DOC.navy }}>
                        UOVBSGSG
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt style={{ color: DOC.muted }}>Reference</dt>
                      <dd className="text-right font-medium" style={{ color: DOC.navy }}>
                        {payNowRef}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {payNowQrValue && (
                <div className="justify-self-center sm:justify-self-end text-center">
                  <QRCodeSVG
                    value={payNowQrValue}
                    size={128}
                    level="H"
                    marginSize={0}
                    bgColor={DOC.cream}
                    fgColor={DOC.navy}
                  />
                  <p className="mt-3 text-[10px]" style={{ color: DOC.muted }}>
                    Scan to PayNow
                  </p>
                  <p className="mt-1 text-[12px] font-semibold tabular-nums">
                    {formatInvoiceMoneySgd(dueForPayNow)}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        <div
          className="mt-12 pt-6 flex flex-col sm:flex-row items-center justify-center gap-3 print:hidden"
          style={{ borderTop: `1px solid ${DOC.rowBorder}` }}
        >
          {vm.onPrintPdf && (
            <button
              type="button"
              onClick={vm.onPrintPdf}
              className="inline-flex items-center gap-2 text-[12px] font-medium"
              style={{ color: DOC.muted }}
            >
              <Download className="h-3.5 w-3.5" />
              Print / save as PDF
            </button>
          )}
          {vm.hasPdf && vm.onDownloadPdf && (
            <button
              type="button"
              onClick={() => void vm.onDownloadPdf?.()}
              className="inline-flex items-center gap-2 text-[12px] font-medium"
              style={{ color: DOC.muted }}
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </button>
          )}
        </div>

        <footer className="mt-8 pt-6 text-center space-y-1.5" style={{ borderTop: `1px solid ${DOC.rowBorder}` }}>
          <p className="text-[10px]" style={{ color: DOC.muted }}>
            CLARITY AV PTE LTD · UEN: {UEN} · GST Reg: {UEN}
          </p>
          <p className="text-[10px]" style={{ color: DOC.muted }}>
            Showroom: No. 1 Coleman Street, #03-36 The Adelphi, Singapore 179803
          </p>
          <p className="text-[10px]" style={{ color: DOC.muted }}>
            Tel: +65 6333-0010
          </p>
          <div className="pt-2">
            {waLink ? (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-green-700"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Please contact {vm.picName || "our team"} on WhatsApp
              </a>
            ) : (
              <p className="text-[10px]" style={{ color: DOC.muted }}>
                Please contact {vm.picName || "our team"} for enquiries.
              </p>
            )}
          </div>
        </footer>
      </article>
    </div>
  );
}
